import { useState } from 'react';
import { Button, useToast } from './ui';
import { api } from '../lib/api';
import AgentMarkdown from './AgentMarkdown';

/**
 * "Prep Call" — runs the backend call-prep agent (POST /api/agents/call-prep)
 * and shows the broker-ready brief in a right slide-over. Reusable on property
 * and contact pages; pass whichever ids the page has.
 */
interface CallPrepButtonProps {
  propertyId?: string;
  contactId?: string;
  ownerName?: string;
  size?: 'sm' | 'md';
  /** `link` renders the design's red text link (the property page's action card). */
  variant?: 'button' | 'link';
}

export default function CallPrepButton({ propertyId, contactId, ownerName, size = 'md', variant = 'button' }: CallPrepButtonProps) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [brief, setBrief] = useState<string | null>(null);
  const { toast } = useToast();

  const run = async () => {
    setOpen(true);
    if (busy) return;
    setBusy(true);
    setBrief(null);
    try {
      const res: any = await api.agentCallPrep({ propertyId, contactId, ownerName });
      setBrief(res.brief || 'No brief returned.');
    } catch (e: any) {
      toast.error('Call prep failed', e?.message);
      setOpen(false);
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    if (!brief) return;
    try {
      await navigator.clipboard.writeText(brief);
      toast.success('Brief copied');
    } catch {
      toast.error('Copy failed');
    }
  };

  return (
    <>
      {variant === 'link' ? (
        <button
          type="button"
          onClick={run}
          className="text-[11.5px] font-semibold leading-none text-primary-500 hover:text-primary-600 hover:underline underline-offset-[3px]"
        >
          Prep call
        </button>
      ) : (
        <Button variant="outline" size={size === 'sm' ? 'sm' : undefined} onClick={run}>
          Prep call
        </Button>
      )}

      {open && (
        <div className="fixed inset-0 z-[1200]" onClick={() => !busy && setOpen(false)}>
          <div className="absolute inset-0 bg-black/40" />
          <div
            className="absolute right-0 top-0 h-full w-full max-w-xl bg-white shadow-2xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4 px-6 py-5 border-b border-gray-100">
              <div>
                <h2 className="font-display text-xl font-semibold uppercase text-gray-900">Call Prep</h2>
                <p className="text-xs text-gray-400 mt-0.5">AI brief from your own data — verify before dialing.</p>
              </div>
              <button
                onClick={() => !busy && setOpen(false)}
                className="text-gray-400 hover:text-gray-600 text-sm mt-1"
              >
                ✕
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-5">
              {busy ? (
                <div className="flex flex-col items-center justify-center py-16 gap-3">
                  <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-primary-500" />
                  <p className="text-sm text-gray-500">Reading the file — owner, loans, tenants…</p>
                  <p className="text-xs text-gray-400">Usually 15–30 seconds.</p>
                </div>
              ) : brief ? (
                <AgentMarkdown text={brief} />
              ) : null}
            </div>

            {brief && !busy && (
              <div className="flex gap-2 px-6 py-4 border-t border-gray-100">
                <Button onClick={copy}>Copy brief</Button>
                <Button variant="outline" onClick={run}>Regenerate</Button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
